import Lang from '/@/modules/core/app/services/Lang';
import { defineRule } from 'vee-validate';
import { between, email, max, numeric, required, size } from '@vee-validate/rules';
import { isFunction } from 'lodash';
import { isValidURL } from '/@/modules/core/app/helpers/StringHelper';
import { fileSize } from '/@/modules/core/app/helpers/FileHelper';

// Add the required rule
defineRule('required', (value, params, ctx) => {
  const result = required(value, params, ctx);
  if (result === true) return true;

  const field = ctx.field;
  return field !== '{field}' ? `The ${field} is required` : Lang.t('core.this-is-required');
});

defineRule('required_nullable', (value) => {
  if (value !== undefined && !Number.isNaN(value)) {
    return true;
  }
  return 'This field is required';
});

defineRule('numeric', (value, params, ctx) => {
  const result = numeric(value, params, ctx);
  if (result === true) return true;

  const field = ctx.field;
  return field !== '{field}' ? `The ${field} is a number` : 'This field is a number';
});

defineRule('email', (value, params, ctx) => {
  const result = email(value, params, ctx);
  if (result === true) return true;

  return Lang.t('core.add-valid-email');
});

defineRule('password', (value) => {
  // make sure the regex matches the backend in UserPasswordRule.php
  if (value.trim() === value && value.match(/^(?=.*[A-Z])(\S{12,})$/)) {
    return true;
  }
  return 'Password must contain at least twelve characters, one capital letter, and no leading or trailing spaces';
});

defineRule('confirm', (value, [password]) => {
  if (value === password) {
    return true;
  }
  return 'Passwords confirmation does not match';
});

/**
 * Validates any url with the regEx specified in 'isValidURL' method
 */
defineRule('url', (value) => {
  if (!isValidURL(value)) {
    return 'Please enter a valid url';
  }
  return true;
});

/**
 * Rule should be in the given format ---> "'between:min,max'"
 * Validates whether a given value is in between mix and max (includes min and max)
 */
defineRule('between', (value, [min, max], ctx) => {
  const result = between(value, { min, max }, ctx);
  if (result === true) return true;

  return `Value should be between ${min} and ${max}`;
});

defineRule('max', (value, [length], ctx) => {
  const result = max(value, { length }, ctx);
  if (result === true) return true;

  return `Text length should be maximum ${length} characters`;
});

/**
 * Rule should be in the given format ---> "'min_value:min'"
 * Validates whether a given value is greater than or equal to min
 */
defineRule('min_value', (value, [min_value]) => {
  if (Number.parseFloat(value) >= min_value) {
    return true;
  }
  return `Value should be minimum ${min_value}`;
});

defineRule('greater_than', (value, [greater_than]) => {
  if (Number.parseFloat(value) > greater_than) {
    return true;
  }
  return Lang.t('core.value-greater-than', { greater_than });
});

defineRule('size', (value, [maxSize], ctx) => {
  const result = size(value, { size: maxSize }, ctx);
  if (result === true) return true;

  return `File size cannot be more than ${fileSize(maxSize * 1024)}`;
});

/**
 * To pass any custom validation function.
 * Check seo/website/websiteForm/competitorStep for an example or check
 * https://github.com/logaretm/vee-validate/issues/2736#issuecomment-617322253
 */
defineRule('custom', (value, [validators]) => {
  const failedValidator = validators?.find((validator) => !validator.validate(value));

  if (!failedValidator) {
    return true;
  }

  if (isFunction(failedValidator.message)) {
    return failedValidator.message(value);
  }
  return failedValidator.message;
});
